import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Linkedin, Facebook, Instagram, Menu, X } from "lucide-react";
import { useTheme } from "./ThemeProvider";

const navLinkClass = (onDark) => ({ isActive }) =>
    `text-[12px] tracking-[0.22em] uppercase font-medium transition-colors duration-300 ${
        isActive
            ? "text-[hsl(var(--brass))]"
            : onDark
              ? "text-[hsl(var(--ivory)/0.85)] hover:text-[hsl(var(--brass))]"
              : "text-[hsl(var(--ink))] hover:text-[hsl(var(--brass))]"
    }`;

export function Header() {
    const loc = useLocation();
    const onHome = loc.pathname === "/";
    const [open, setOpen] = useState(false);
    const { theme } = useTheme();
    const phoneDisplay = theme.phone_display || "—";
    const phoneNumber = (theme.phone_number || "").replace(/[^\d+]/g, "") || "#";

    // Close the mobile drawer whenever the route changes
    useEffect(() => {
        setOpen(false);
    }, [loc.pathname]);

    // Lock body scroll while drawer is open
    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => {
            document.body.style.overflow = "";
        };
    }, [open]);

    return (
        <header
            className="absolute top-0 left-0 right-0 z-30"
            style={{ background: "transparent" }}
            data-testid="site-header"
        >
            <div className="container-prose flex items-center justify-between py-6">
                <Link to="/" className="reveal-slow flex items-center gap-3" data-testid="brand-logo">
                    <img
                        src={onHome ? "https://res.cloudinary.com/datmqdk2c/image/upload/f_auto,q_auto/lc/brand/lc-logo-dark-bg.png" : "https://res.cloudinary.com/datmqdk2c/image/upload/f_auto,q_auto/lc/brand/lc-logo-light-bg.png"}
                        alt="Les Concierges® — Anything. Anytime. Anywhere."
                        className="h-12 md:h-16 w-auto"
                        style={{ objectFit: "contain" }}
                    />
                </Link>
                <nav className="hidden md:flex items-center gap-9" data-testid="primary-nav">
                    <NavLink to="/" end className={navLinkClass(onHome)} data-testid="nav-home">
                        Home
                    </NavLink>
                    <NavLink to="/about" className={navLinkClass(false)} data-testid="nav-about">
                        About
                    </NavLink>
                    <NavLink to="/solutions" className={navLinkClass(false)} data-testid="nav-solutions">
                        Solutions
                    </NavLink>
                    <NavLink to="/connect" className={navLinkClass(false)} data-testid="nav-connect">
                        Connect
                    </NavLink>
                </nav>
                <Link
                    to="/connect"
                    className={`header-cta-desktop btn-ghost ${onHome ? "btn-ghost-on-dark" : ""}`}
                    style={{ padding: "0.55rem 1.05rem", fontSize: 12 }}
                    data-testid="header-cta-talk"
                >
                    Speak to us
                </Link>

                {/* Mobile hamburger */}
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    aria-label="Open menu"
                    className="md:hidden inline-flex items-center justify-center w-11 h-11 border"
                    style={{
                        borderColor: "hsl(var(--brass) / 0.5)",
                        color: onHome ? "hsl(var(--ivory))" : "hsl(var(--ink))",
                    }}
                    data-testid="mobile-menu-open"
                >
                    <Menu size={18} strokeWidth={1.5} />
                </button>
            </div>

            {!onHome && (
                <div
                    className="gold-rule reveal-rule mx-6 md:mx-12"
                    style={{ background: "hsl(var(--brass) / 0.25)" }}
                />
            )}

            {/* Mobile drawer — full-screen navy panel */}
            {open && (
                <div
                    className="fixed inset-0 z-50 md:hidden flex flex-col"
                    style={{ background: "hsl(var(--navy))", color: "hsl(var(--ivory))" }}
                    data-testid="mobile-menu-drawer"
                >
                    <div className="flex items-center justify-between px-6 py-6">
                        <Link
                            to="/"
                            onClick={() => setOpen(false)}
                            className="flex items-center"
                            data-testid="mobile-brand-logo"
                        >
                            <img
                                src="https://res.cloudinary.com/datmqdk2c/image/upload/f_auto,q_auto/lc/brand/lc-logo-dark-bg.png"
                                alt="Les Concierges®"
                                className="h-12 w-auto"
                                style={{ objectFit: "contain" }}
                            />
                        </Link>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            aria-label="Close menu"
                            className="inline-flex items-center justify-center w-11 h-11 border"
                            style={{ borderColor: "hsl(var(--brass) / 0.5)", color: "hsl(var(--ivory))" }}
                            data-testid="mobile-menu-close"
                        >
                            <X size={18} strokeWidth={1.5} />
                        </button>
                    </div>

                    <nav className="flex-1 flex flex-col px-6 pt-6 gap-0.5" data-testid="mobile-primary-nav">
                        {[
                            { to: "/", label: "Home", tid: "mobile-nav-home", end: true },
                            { to: "/about", label: "About", tid: "mobile-nav-about" },
                            { to: "/solutions", label: "Solutions", tid: "mobile-nav-solutions" },
                            { to: "/connect", label: "Connect", tid: "mobile-nav-connect" },
                        ].map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.end}
                                onClick={() => setOpen(false)}
                                className={({ isActive }) =>
                                    `block py-5 border-b text-[28px] font-serif transition-colors ${
                                        isActive
                                            ? "text-[hsl(var(--brass))]"
                                            : "text-[hsl(var(--ivory))] hover:text-[hsl(var(--brass))]"
                                    }`
                                }
                                style={{ borderColor: "hsl(var(--brass) / 0.25)" }}
                                data-testid={l.tid}
                            >
                                {l.label}
                            </NavLink>
                        ))}

                        <Link
                            to="/connect"
                            onClick={() => setOpen(false)}
                            className="mt-10 inline-flex items-center justify-center py-4 border"
                            style={{
                                borderColor: "hsl(var(--brass))",
                                color: "hsl(var(--brass))",
                                fontSize: 12,
                                letterSpacing: "0.22em",
                                textTransform: "uppercase",
                            }}
                            data-testid="mobile-cta-talk"
                        >
                            Request a private pilot
                        </Link>
                    </nav>

                    <div className="px-6 py-8 border-t" style={{ borderColor: "hsl(var(--brass) / 0.25)" }}>
                        <p className="text-[11px] tracking-[0.22em] uppercase" style={{ color: "hsl(var(--ivory) / 0.55)" }}>
                            Speak to us directly
                        </p>
                        <p className="mt-2 text-[14px] leading-relaxed" style={{ color: "hsl(var(--ivory) / 0.85)" }}>
                            <a
                                href={phoneNumber === "#" ? "#" : `tel:${phoneNumber}`}
                                className="hover:text-[hsl(var(--brass))]"
                                data-testid="mobile-drawer-phone"
                            >
                                {phoneDisplay}
                            </a>
                            <span style={{ color: "hsl(var(--ivory) / 0.55)" }}> · Les Concierges®</span>
                        </p>
                    </div>
                </div>
            )}
        </header>
    );
}

export function Footer() {
    const { theme } = useTheme();
    const phoneDisplay = theme.phone_display || "—";
    const phoneNumber = (theme.phone_number || "").replace(/[^\d+]/g, "") || "#";
    const inboundEmail = theme.inbound_email || "enquiries@lesconcierges.co.in";
    return (
        <footer
            className="mt-0"
            style={{ background: "hsl(var(--navy))", color: "hsl(var(--ivory))" }}
            data-testid="site-footer"
        >
            <div className="container-prose py-20 grid md:grid-cols-3 gap-12">
                <div>
                    <img
                        src="https://res.cloudinary.com/datmqdk2c/image/upload/f_auto,q_auto/lc/brand/lc-logo-dark-bg.png"
                        alt="Les Concierges® — Anything. Anytime. Anywhere."
                        className="h-20 w-auto"
                        style={{ objectFit: "contain" }}
                    />
                    <p
                        className="mt-8 max-w-xs leading-relaxed text-[14px]"
                        style={{ color: "hsl(var(--ivory) / 0.75)" }}
                    >
                        Twenty-eight years of human concierge® legacy. Now, with the most considered AI in
                        India behind every request — anywhere your people are.
                    </p>
                    <p className="mt-6 eyebrow" style={{ color: "hsl(var(--brass-soft))" }}>
                        The AI listens. The humans deliver.
                    </p>

                    <div className="mt-8 flex items-center gap-5" data-testid="footer-socials">
                        <a
                            href="https://www.linkedin.com/company/les-concierges-services-private-limited/"
                            target="_blank"
                            rel="noreferrer"
                            aria-label="Les Concierges on LinkedIn"
                            className="inline-flex items-center justify-center w-9 h-9 border border-[hsl(var(--brass)/0.5)] hover:border-[hsl(var(--brass))] hover:text-[hsl(var(--brass))] transition-colors"
                            style={{ color: "hsl(var(--ivory) / 0.75)" }}
                            data-testid="social-linkedin"
                        >
                            <Linkedin size={15} strokeWidth={1.5} />
                        </a>
                        <a
                            href="https://www.instagram.com/lesconcierges"
                            target="_blank"
                            rel="noreferrer"
                            aria-label="Les Concierges on Instagram"
                            className="inline-flex items-center justify-center w-9 h-9 border border-[hsl(var(--brass)/0.5)] hover:border-[hsl(var(--brass))] hover:text-[hsl(var(--brass))] transition-colors"
                            style={{ color: "hsl(var(--ivory) / 0.75)" }}
                            data-testid="social-instagram"
                        >
                            <Instagram size={15} strokeWidth={1.5} />
                        </a>
                        <a
                            href="https://www.facebook.com/mylesconcierges"
                            target="_blank"
                            rel="noreferrer"
                            aria-label="Les Concierges on Facebook"
                            className="inline-flex items-center justify-center w-9 h-9 border border-[hsl(var(--brass)/0.5)] hover:border-[hsl(var(--brass))] hover:text-[hsl(var(--brass))] transition-colors"
                            style={{ color: "hsl(var(--ivory) / 0.75)" }}
                            data-testid="social-facebook"
                        >
                            <Facebook size={15} strokeWidth={1.5} />
                        </a>
                    </div>
                </div>
                <div>
                    <p className="eyebrow mb-4" style={{ color: "hsl(var(--brass-soft))" }}>
                        Three brands, one roof
                    </p>
                    <ul className="space-y-2 text-[14px]" style={{ color: "hsl(var(--ivory) / 0.85)" }}>
                        <li>Les Concierges® — Corporate</li>
                        <li>Club Concierge® — Lifestyle &amp; HNI</li>
                        <li>The Doggy Company — Pet life OS, May 2026</li>
                    </ul>
                </div>
                <div>
                    <p className="eyebrow mb-4" style={{ color: "hsl(var(--brass-soft))" }}>
                        For evaluation conversations
                    </p>
                    <p className="text-[14px] leading-relaxed" style={{ color: "hsl(var(--ivory) / 0.85)" }}>
                        <a
                            href={`mailto:${inboundEmail}`}
                            className="underline-offset-4 hover:underline text-[hsl(var(--brass-soft))]"
                            data-testid="footer-email"
                        >
                            {inboundEmail}
                        </a>
                    </p>
                    <p className="mt-3 text-[14px] leading-relaxed" style={{ color: "hsl(var(--ivory) / 0.85)" }}>
                        <a
                            href={phoneNumber === "#" ? "#" : `tel:${phoneNumber}`}
                            className="hover:text-[hsl(var(--brass))] transition-colors"
                            data-testid="footer-phone"
                        >
                            {phoneDisplay}
                        </a>
                        <span style={{ color: "hsl(var(--ivory) / 0.55)" }}> · Les Concierges®</span>
                    </p>
                </div>
            </div>
            <div
                className="gold-rule"
                style={{ background: "hsl(var(--brass) / 0.35)", margin: "0 1.5rem" }}
            />
            <div
                className="container-prose py-8 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-[12px]"
                style={{ color: "hsl(var(--ivory) / 0.55)" }}
            >
                <span>
                    © {new Date().getFullYear()} Les Concierges® Services Pvt Ltd · Holders of the registered concierge® mark of India
                </span>
                <span>Funded by Rare Enterprise · A Rakesh Jhunjhunwala company</span>
            </div>
        </footer>
    );
}

export default function Layout({ children }) {
    return (
        <div className="App min-h-screen">
            <Header />
            <main>{children}</main>
            <Footer />
        </div>
    );
}
